import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import idConfig from 'utils/ecommerce/services/id.config';
import { env } from 'config/app.config';
import { finalize, single } from 'rxjs/operators';
import ActivateInternationalMinutesDialog from './subcomponents/ActivateInternationalMinutesDialog';
import { translate } from 'components/Translation';
import AccordionContent from '../../../common/AccordionContent';
import InternationalMinutesTooltip from './subcomponents/InternationalMinutesTooltip';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/servicesStyle';
import { makeStyles } from '@material-ui/core';
import { Package } from 'utils/api/models/numbers/usage';
import { CustomAttribute, extractAttributeValuesAll } from 'utils/ecommerce/services/extract';
import { NumbersService } from 'services/numbers/numbers';
import { SingleDialingCode } from 'utils/api/models/numbers/singleDialingCode';
import { getProductId } from 'views/Numbers/Details/Services/helpers/getProductId';
import RoamingInternationalActivationDialog from '../RoamingInternationalActivationDialog';
import { SingleSubscriberProduct } from 'utils/api/models/subscriberProducts/singleSubscriberProduct';
import { SingleOrderStatus } from 'utils/api/models/orderTracking/singleOrderStatus';
import { internationalMinutesPackageDescription } from 'views/Numbers/Details/Services/subcomponents/MobilePostpaid/subcomponents/JawazakContentWrapper/helpers';
import { getLanguage } from 'utils/language/language';

// @ts-ignore
const useStyles = makeStyles(styles);

type dialog = 'planActivationDialog' | 'topUpDialog' | 'changePlanDialog';

interface IInternationalMinutesAccordionWrapperProps {
	termsAndConditions: types.services.termsAndConditions;
	updateOrderItems: types.services.updateOrderItemsFunction;
	internationalEnabled: boolean;
	packages: Package[];
	setPendingStatus: (status: boolean) => void;
	internationalPending: boolean;
	subscriberProducts: SingleSubscriberProduct[];
	orderStatuses: SingleOrderStatus[];
	setIsInternationalMinutesActive: React.Dispatch<React.SetStateAction<boolean>>;
}

const InternationalMinutesAccordionWrapper = ({
	termsAndConditions,
	updateOrderItems,
	internationalEnabled,
	packages,
	setPendingStatus,
	internationalPending,
	subscriberProducts,
	orderStatuses,
	setIsInternationalMinutesActive,
}: IInternationalMinutesAccordionWrapperProps) => {
	const classes = useStyles();
	const isMounted = useRef(true);
	const [planCatalogEntryView, setPlanCatalogEntryView] = useState<types.ecommerce.CatalogEntryView[]>([]);
	const [topUpCatalogEntryView, setTopUpCatalogEntryView] = useState<types.ecommerce.CatalogEntryView[]>([]);
	const [internationalDialogOpen, setInternationalDialogOpen] = useState<boolean>(false);
	const [countries, setCountries] = useState<string[]>([]);
	const [dialingCodes, setDialingCodes] = useState<SingleDialingCode[]>([]);
	const [dialogType, setDialogType] = useState<dialog | null>(null);
	const [loadingPlans, setLoadingPlans] = useState<boolean>(true);
	const [loadingTopUps, setLoadingTopUps] = useState<boolean>(true);

	const language = getLanguage();

	const hideDialog = () => {
		setDialogType(null);
	};

	const showPlanActivationDialog = () => {
		setDialogType('planActivationDialog');
	};

	const showTopUpDialog = () => {
		setDialogType('topUpDialog');
	};

	const showChangePlanDialog = () => {
		setDialogType('changePlanDialog');
	};

	const showInternationalDialog = () => {
		setInternationalDialogOpen(true);
	};

	const hideInternationalDialog = () => {
		setInternationalDialogOpen(false);
	};

	const getCatalogEntryView = useCallback((response: any): types.ecommerce.CatalogEntryView[] => {
		const _catalogEntryView = response.catalogEntryView;
		if (_catalogEntryView && Array.isArray(_catalogEntryView) && _catalogEntryView.length > 0) {
			return _catalogEntryView;
		}
		return [];
	}, []);

	useEffect(() => {
		setLoadingPlans(true);
		ECommerceService.getGroupContent(idConfig.SERVICES_TABLES.INTERNATIONAL_MINUTES_PLAN[env])
			.pipe(finalize(() => setLoadingPlans(false)))
			.subscribe(({ response }: any) => {
				setPlanCatalogEntryView(getCatalogEntryView(response));
			});
	}, [getCatalogEntryView]);

	useEffect(() => {
		setLoadingTopUps(true);
		ECommerceService.getGroupContent(idConfig.SERVICES_TABLES.INTERNATIONAL_MINUTES_TOP_UP[env])
			.pipe(finalize(() => setLoadingTopUps(false)))
			.subscribe(({ response }: any) => {
				setTopUpCatalogEntryView(getCatalogEntryView(response));
			});
	}, [getCatalogEntryView]);

	const pendingSubscriberProductIds = useMemo(
		() => subscriberProducts.filter(({ status }) => status !== 'Active').map(({ productId }) => productId),
		[subscriberProducts]
	);

	const validateReqProd = useCallback(
		(singleCatalogEntryView: types.ecommerce.CatalogEntryView) => {
			const subscriberProductIds = subscriberProducts.map(({ productId }) => productId);
			const { attributes } = singleCatalogEntryView;
			const reqProdIds = extractAttributeValuesAll(
				{ attributes },
				CustomAttribute.REQ_PROD
			).map((singleAttribute) => getProductId(singleAttribute));
			for (let i = 0; i < reqProdIds.length; i++) {
				if (subscriberProductIds.indexOf(reqProdIds[i]) !== -1) {
					return true;
				}
			}
			return false;
		},
		[subscriberProducts]
	);

	const pendingTopUpPackages: types.ecommerce.CatalogEntryView[] = useMemo(() => {
		return topUpCatalogEntryView.filter(({ partNumber }) => {
			return pendingSubscriberProductIds?.indexOf(getProductId(partNumber)) >= 0;
		});
	}, [topUpCatalogEntryView, pendingSubscriberProductIds]);

	const pendingPlanPackages: types.ecommerce.CatalogEntryView[] = useMemo(() => {
		return planCatalogEntryView.filter(({ partNumber }) => {
			return pendingSubscriberProductIds?.indexOf(getProductId(partNumber)) >= 0;
		});
	}, [planCatalogEntryView, pendingSubscriberProductIds]);

	const filteredTopUpCatalogEntryView: types.ecommerce.CatalogEntryView[] = useMemo(() => {
		return topUpCatalogEntryView.filter((singleTopUpCatalogEntryView) => {
			return validateReqProd(singleTopUpCatalogEntryView);
		});
	}, [validateReqProd, topUpCatalogEntryView]);

	const activePlans: types.ecommerce.CatalogEntryView[] | undefined = useMemo(() => {
		const activeProductsIds = subscriberProducts.map(
			(singleSubscriberProduct) => singleSubscriberProduct.productId
		);

		return planCatalogEntryView.filter((singleCatalogEntryView) =>
			activeProductsIds.includes(getProductId(singleCatalogEntryView.partNumber))
		);
	}, [planCatalogEntryView, subscriberProducts]);

	const filteredPlanCatalogEntryView: types.ecommerce.CatalogEntryView[] = useMemo(() => {
		return planCatalogEntryView.filter((singlePlanCatalogEntryView) => {
			return (
				validateReqProd(singlePlanCatalogEntryView) &&
				!activePlans?.some((activePlan) => activePlan.partNumber === singlePlanCatalogEntryView.partNumber)
			);
		});
	}, [validateReqProd, planCatalogEntryView, activePlans]);

	const turnOffInternationalMinutes = (singlePackage: types.services.IPackage) => {
		const orderItem: types.services.IOrderItem = {
			action: 'REMOVE',
			id: singlePackage.legacyProductId,
			place: singlePackage.place,
			ebuConfigProduct: singlePackage.ebuConfigProduct,
		};
		updateOrderItems([orderItem], 'TURN_OFF', singlePackage.name, true);
	};

	const updateInternationalMinutes = (singlePackage: types.services.IPackage) => {
		if (singlePackage.status?.type === 'ACTIVE' && singlePackage.id !== 'INTERNATIONAL_MINUTES') {
			turnOffInternationalMinutes(singlePackage);
		} else if (internationalEnabled) {
			showPlanActivationDialog();
		} else {
			showInternationalDialog();
		}
	};

	const properCatalogEntryView: types.ecommerce.CatalogEntryView[] = useMemo(() => {
		switch (dialogType) {
			case null:
				return [];
			case 'topUpDialog':
				return filteredTopUpCatalogEntryView;
			default:
				return filteredPlanCatalogEntryView;
		}
	}, [dialogType, filteredPlanCatalogEntryView, filteredTopUpCatalogEntryView]);

	const activeTopUps: Package[] = useMemo(() => {
		return packages.filter((singlePackage) => {
			const productType = singlePackage.commerceAttributes?.productType;
			return productType === 'INT_MIN_TOPUP';
		});
	}, [packages]);

	const getPlanName = (plan: types.ecommerce.CatalogEntryView | undefined) => {
		if (plan) {
			const price = plan.price.find((singlePrice) => singlePrice.usage === 'Offer');
			return `${plan.name} ${translate('number.services.price', price.value, price.currency)}`;
		}
		return '';
	};

	const turnOnPlan = () => {
		if (internationalEnabled) {
			showPlanActivationDialog();
		} else {
			showInternationalDialog();
		}
	};

	const topUpPlan = () => {
		if (internationalEnabled) {
			showTopUpDialog();
		} else {
			showInternationalDialog();
		}
	};

	const internationalMinutesPackages = useMemo(() => {
		const isPending: boolean = pendingPlanPackages.length > 0 || pendingTopUpPackages.length > 0;
		const pendingPlanPackagesIds = pendingPlanPackages.map(({ partNumber }) => getProductId(partNumber));
		const activePendingPackages =
			!!activePlans &&
			subscriberProducts.filter(({ productId, status }) => {
				return status !== 'Active' && pendingPlanPackagesIds.indexOf(productId) >= 0;
			});
		const activeStatus: types.services.IStatus = {
			type: 'ACTIVE',
		};
		const pendingStatus: types.services.IStatus = {
			type: 'PENDING',
		};

		const showTooltip = countries.length > 0 && dialingCodes.length > 0;
		return [
			{
				name: translate('services.gsm.international.minutes'),
				id: 'INTERNATIONAL_MINUTES',
				status:
					isPending || activePlans
						? undefined
						: {
								type: 'INACTIVE',
								text: translate('badge.inactive'),
						  },
				legacyProductId: '',
				place: '',
				description: translate('services.gsm.international.minutes.description'),
				tooltip: showTooltip
					? {
							classes: classes.internationalMinutesTooltip,
							content: <InternationalMinutesTooltip countries={countries} dialingCodes={dialingCodes} />,
					  }
					: undefined,
				buttons: [
					filteredTopUpCatalogEntryView.length > 0 && {
						text: translate('number.services.top.up'),
						color: 'primary-light',
						action: topUpPlan,
						type: 'TOP_UP',
					},

					filteredPlanCatalogEntryView.length > 0 && {
						text: translate('numbers.services.turn.on'),
						color: 'primary-light',
						action: turnOnPlan,
						type: 'TURN_ON',
					},
				].filter(Boolean),
			},

			...activeTopUps.map((singleActiveTopUp) => {
				const activePendingTopUp = subscriberProducts.find(({ productId, status }) => {
					return (
						status !== 'Active' &&
						(productId === getProductId(singleActiveTopUp.packageId) ||
							productId === getProductId(singleActiveTopUp.commerceUniqueIdToActivate))
					);
				});
				return {
					name: singleActiveTopUp.name,
					description: internationalMinutesPackageDescription(singleActiveTopUp),
					id: singleActiveTopUp.packageId,
					status: activePendingTopUp
						? { ...pendingStatus, date: activePendingTopUp.startDate }
						: activeStatus,
					legacyProductId: '',
					place: '',
					buttons: [],
				};
			}),
			...activePlans.map((singleActivePlan) => {
				const activePendingPackage = activePendingPackages.find(
					(app) => app.productId === singleActivePlan.partNumber
				);
				const definitionSource = singleActivePlan.attributes.find((attr) => attr.name === 'DEFINITION_SOURCE');
				const ebuConfigProduct = singleActivePlan.attributes.find((attr) => attr.name === 'EBU_CONFIG_PRODUCT');
				if (
					definitionSource &&
					definitionSource.values[0] &&
					definitionSource.values[0].value &&
					ebuConfigProduct &&
					ebuConfigProduct.values[0] &&
					ebuConfigProduct.values[0].value
				) {
					return {
						name: translate('services.gsm.international.minutes'),
						description: getPlanName(singleActivePlan),
						id: singleActivePlan.uniqueID,
						legacyProductId: singleActivePlan.partNumber,
						place: singleActivePlan.attributes.find((attr) => attr.name === 'DEFINITION_SOURCE').values[0]
							.value,
						ebuConfigProduct: singleActivePlan.attributes.find((attr) => attr.name === 'EBU_CONFIG_PRODUCT')
							.values[0].value,
						status: !!activePendingPackage
							? { ...pendingStatus, date: activePendingPackage.startDate }
							: { ...activeStatus, hidden: false },
						buttons: [
							{
								text: translate('numbers.services.turn.off'),
								color: 'tertiary-light',
								disabled: !!activePendingPackage,
								type: 'TURN_OFF',
							},
						],
					};
				}
			}),
		].filter(Boolean) as types.services.IPackage[];
		// eslint-disable-next-line
	}, [
		countries,
		dialingCodes,
		subscriberProducts,
		orderStatuses,
		activePlans,
		activeTopUps,
		filteredTopUpCatalogEntryView,
		filteredPlanCatalogEntryView,
		pendingTopUpPackages,
		pendingPlanPackages,
		language,
	]);

	useEffect(() => {
		NumbersService.getInternationalMinutesCountries().subscribe(({ countries }) => {
			if (isMounted.current) {
				setCountries(countries);
			}
		});
	}, []);

	useEffect(() => {
		NumbersService.getInternationalMinutesDialingCodes().subscribe(({ dialingCodes }) => {
			if (isMounted.current) {
				setDialingCodes(dialingCodes);
			}
		});
	}, []);

	useEffect(() => {
		setPendingStatus(pendingTopUpPackages.length > 0 || pendingPlanPackages.length > 0);
		// eslint-disable-next-line
	}, [pendingTopUpPackages, pendingPlanPackages]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const activeCatalogEntryView: types.ecommerce.CatalogEntryView[] | undefined = useMemo(() => {
		if (dialogType === 'changePlanDialog' || dialogType === 'planActivationDialog') {
			if (activePlans) {
				return activePlans;
			}
		}
		return undefined;
	}, [activePlans, dialogType]);

	const dialogHeader: string = useMemo(() => {
		switch (dialogType) {
			case 'changePlanDialog':
				return translate('number.services.international.change.plan');
			case 'planActivationDialog':
				return translate('number.services.international.activation');
			case 'topUpDialog':
				return translate('number.services.international.top.up');
			default:
				return '';
		}
	}, [dialogType]);

	const buttonText: string = useMemo(() => {
		switch (dialogType) {
			case 'changePlanDialog':
				return translate('change');
			case 'planActivationDialog':
				return translate('turnOn');
			case 'topUpDialog':
				return translate('number.services.top.up');
			default:
				return '';
		}
	}, [dialogType]);

	useEffect(() => {
		setIsInternationalMinutesActive(internationalMinutesPackages.some((pckg) => pckg.status?.type === 'ACTIVE'));
	}, [internationalMinutesPackages]);

	return (
		<>
			<AccordionContent
				elements={internationalMinutesPackages}
				termsAndConditions={termsAndConditions}
				updateService={updateInternationalMinutes}
				confirmations={[{ type: 'TURN_OFF' }]}
			/>
			<ActivateInternationalMinutesDialog
				isOpen={dialogType !== null}
				hideDialog={hideDialog}
				termsAndConditions={termsAndConditions}
				catalogEntryView={properCatalogEntryView}
				updateOrderItems={updateOrderItems}
				loading={loadingPlans || loadingTopUps}
				countries={countries}
				activeCatalogEntryView={activeCatalogEntryView}
				header={dialogHeader}
				buttonText={buttonText}
				additionalText={dialogType === 'changePlanDialog' ? translate('number.services.downgrade') : ''}
			/>
			<RoamingInternationalActivationDialog
				isOpen={internationalDialogOpen}
				hideDialog={hideInternationalDialog}
				type="INTERNATIONAL"
				updateOrderItems={updateOrderItems}
				isPending={internationalPending}
			/>
		</>
	);
};

export default InternationalMinutesAccordionWrapper;
